import type { ScrollToIndexOpts } from "virtua";
import type { Virtualizer } from "virtua/vue";
import { computed, type MaybeRefOrGetter, nextTick, onMounted, onUnmounted, type Ref, ref, toValue, watch } from "vue";

interface VirtuaLoopOptions {
	align?: "start" | "center";
	/** @default 'loop' */
	mode?: MaybeRefOrGetter<"loop" | "virtual">;
	initialIndex?: MaybeRefOrGetter<number>;
}

interface VirtuaListHandle extends InstanceType<typeof Virtualizer> {
	$el?: HTMLElement;
	scrollSize: number;
	viewportSize: number;
	scrollOffset: number;
	findItemIndex: (offset: number) => number;
	getItemOffset: (index: number) => number;
	getItemSize: (index: number) => number;
	scrollToIndex: (index: number, opts?: ScrollToIndexOpts) => void;
	scrollTo: (offset: number) => void;
}

interface UseVirtuaLoopResult {
	listRef: Ref<VirtuaListHandle | null>;
	items: Readonly<Ref<number[]>>;
	hasScroll: Readonly<Ref<boolean>>;
	isReady: Readonly<Ref<boolean>>;
	realIndex: (vIndex: number) => number;
	findNearestIndex: (currentVIndex: number, targetRealIndex: number) => number;
	scrollToIndex: (index: number, opts?: ScrollToIndexOpts) => void;
	init: () => Promise<void>;
	onScroll: () => void;
}

const waitForLayoutStabilization = (list: VirtuaListHandle, maxTries = 10) => {
	return new Promise<void>((resolve) => {
		let stableFrames = 0;
		let tries = 0;
		let lastSize = 0;

		const check = () => {
			if (!list || list.scrollSize === undefined) {
				resolve();
				return;
			}
			const current = list.scrollSize;
			if (current === lastSize && current > 0) stableFrames++;
			else {
				stableFrames = 0;
				lastSize = current;
			}

			tries++;

			if (stableFrames >= 2 || tries >= maxTries) resolve();
			else requestAnimationFrame(check);
		};

		requestAnimationFrame(check);
	});
};

export function useVirtuaLoop<T>(source: MaybeRefOrGetter<T[]>, options?: VirtuaLoopOptions): UseVirtuaLoopResult {
	const align = options?.align ?? "start";

	let canLoop = false;

	const listRef = ref<VirtuaListHandle | null>(null);
	const hasScroll = ref(false);
	const isReady = ref(false);
	const mode = ref<"measure" | VirtuaLoopOptions["mode"]>("measure");

	const size = computed(() => toValue(source).length);
	const copies = computed(() => (mode.value === "measure" || mode.value === "virtual" ? 1 : 5));
	const items = computed(() => Array.from({ length: size.value * copies.value }, (_, i) => i));
	const initialIndex = computed(() => toValue(options?.initialIndex) || size.value * 3);

	/**
	 * Init measerement
	 */
	const init = async () => {
		if (!listRef.value) return;

		isReady.value = false;
		mode.value = "measure";
		canLoop = false;
		hasScroll.value = false;

		await nextTick();

		if (!listRef.value || mode.value !== "measure") {
			isReady.value = true;
			return;
		}

		await waitForLayoutStabilization(listRef.value);

		if (!listRef.value) {
			isReady.value = true;
			return;
		}

		hasScroll.value = listRef.value.scrollSize > listRef.value.viewportSize;

		if (!hasScroll.value) {
			canLoop = false;
			isReady.value = true;
			return;
		}

		canLoop = true;
		mode.value = toValue(options?.mode) || "loop";

		await nextTick();

		if (!listRef.value) {
			isReady.value = true;
			return;
		}
		listRef.value.scrollToIndex(mode.value === "loop" ? initialIndex.value : (toValue(options?.initialIndex) ?? 0), { align });
		isReady.value = true;
	};

	/**
	 * Get real index by virtual
	 * @param {number} vIndex - Virtual index
	 * @returns {number} Real index
	 */
	const realIndex = (vIndex: number) => {
		if (mode.value === "measure") return vIndex;
		if (size.value === 0) return 0;
		return vIndex % size.value;
	};

	/**
	 * Scroll to the item specified by index.
	 * @param {number} index — index of item
	 * @param {ScrollToIndexOpts} opts - options
	 */
	const scrollToIndex = (index: number, opts?: ScrollToIndexOpts) => {
		if (!listRef.value) return;
		listRef.value.scrollToIndex(index, opts);
	};

	/**
	 * Find nearest virtual index by passing current vIndex and target real index
	 * @param {number} currentVIndex - Current virtual index
	 * @param {number} targetRealIndex - Target real index
	 * @returns {number} nearest virtual index
	 */
	const findNearestIndex = (currentVIndex: number, targetRealIndex: number) => {
		if (!canLoop || mode.value !== "loop") return targetRealIndex;
		return targetRealIndex + Math.round((currentVIndex - targetRealIndex) / size.value) * size.value;
	};

	/**
	 * Calculate safe limits and scroll to main copy
	 * @param vIndex - target virtual index
	 */
	const normalizeByVIndex = (vIndex: number) => {
		if (!canLoop || mode.value !== "loop" || !listRef.value) return;

		const min = size.value - 1;
		const max = size.value * 4;

		if (vIndex < min) return scrollToIndex(size.value * 3 - 1, { align: "start" });
		if (vIndex >= max) return scrollToIndex(size.value * 3, { align: "start" });
	};

	/**
	 * Helper for `normalizeByVIndex`
	 */
	const onScroll = () => {
		if (!listRef.value || !canLoop) return;
		const startIndex = listRef.value.findItemIndex(listRef.value.scrollOffset);

		normalizeByVIndex(startIndex);
	};

	onMounted(init);
	onUnmounted(() => (listRef.value = null));

	watch(size, () => nextTick(init));
	watch(
		() => options?.mode,
		() => init,
	);

	return {
		listRef,
		items,
		hasScroll,
		isReady,
		realIndex,
		findNearestIndex,
		scrollToIndex,
		init,
		onScroll,
	};
}
